<template>
  <div 
    :class="[
      'group bg-purple-700 text-white rounded-lg shadow-lg min-w-[220px] border-2 transition-all relative overflow-visible',
      selected ? 'border-cyan-400 shadow-cyan-400/50' : data.__validationError ? 'border-red-500' : 'border-purple-800'
    ]"
  >
    <!-- Header -->
    <div class="bg-purple-800 px-3 py-2 rounded-t-lg flex items-center gap-2 justify-between">
      <div class="flex items-center gap-2">
        <span class="material-icons text-sm">contact_support</span>
        <span class="font-semibold text-sm">{{ $t('flowEditor.nodes.botAsksNode.title') }}</span>
      </div>
      <ValidationMarker :error="data.__validationError" />
    </div>
    
    <!-- Body -->
    <div class="px-3 py-3 flex flex-col gap-2 text-sm">
      <div class="font-medium truncate" v-tooltip="data.question || $t('flowEditor.nodes.botAsksNode.emptyQuestion')">
        {{ data.question || $t('flowEditor.nodes.botAsksNode.emptyQuestion') }}
      </div>
      <div class="text-purple-200 text-xs flex items-center gap-1">
        <span class="material-icons" style="font-size: 14px;">{{ getResponseIcon(data.responseType) }}</span>
        <span>{{ getResponseLabel(data.responseType) }}</span>
      </div>
      <div v-if="data.variableName" class="text-purple-300 text-xs">
        → {{ data.variableName }}
      </div>
      <div v-if="hasOptions" class="text-xs text-purple-200 mt-2">
        <div class="font-medium mb-1">{{ $t('flowEditor.nodes.botAsksNode.optionsLabel') }}</div>
        <div 
          v-for="(option, idx) in data.options" 
          :key="idx" 
          class="truncate transition-all"
          :class="{ 'bg-cyan-500/30 font-semibold': hoveredOption === option.id }"
          @mouseenter="hoveredOption = option.id"
          @mouseleave="hoveredOption = null"
        >
          <span v-if="useNumbering">{{ idx + 1 }}.</span>
          <span v-else>•</span>
          {{ option.label }}
        </div>
        <div 
          v-if="data.allowFreeText" 
          class="text-purple-300 italic mt-1 transition-all"
          :class="{ 'bg-orange-500/30 font-semibold': hoveredOption === 'other' }"
          @mouseenter="hoveredOption = 'other'"
          @mouseleave="hoveredOption = null"
        >
          {{ $t('flowEditor.nodes.botAsksNode.freeTextLabel') }}
        </div>
      </div>
    </div>

    <!-- Target Handle (input, left) -->
    <Handle
      type="target"
      :position="Position.Left"
      class="w-3 h-3 !bg-purple-400 !border-2 !border-white"
    />

    <!-- Dynamic Source Handles (output, right) with labels -->
    <template v-if="hasOptions">
      <!-- For multiselect: by default show a single combined handle, unless createOptionHandles is enabled -->
      <template v-if="data.responseType === 'multiselect' && !data.createOptionHandles">
        <div
          class="absolute flex items-center gap-1"
          :style="{ top: `${calculateHandleTop(0)}px`, right: '-8px' }"
          @mouseenter="hoveredOption = 'multiselect'"
          @mouseleave="hoveredOption = null"
        >
          <Handle
            id="handle_multiselect"
            type="source"
            :position="Position.Right"
            :class="[
              'w-3 h-3 !border-2 !border-white transition-all',
              hoveredOption === 'multiselect' ? '!bg-cyan-300 !w-4 !h-4 !shadow-lg !shadow-cyan-400/50' : '!bg-cyan-400'
            ]"
            v-tooltip.top="data.question || $t('flowEditor.nodes.botAsksNode.responseType.multiselect')"
          />
          <span
            class="text-[10px] bg-purple-900/90 px-1 rounded-sm whitespace-nowrap pointer-events-none transition-opacity"
            :class="hoveredOption === 'multiselect' ? 'opacity-100' : 'opacity-70'"
          >
            {{ $t('flowEditor.nodes.botAsksNode.responseType.multiselect') }}
          </span>
        </div>

        <div
          v-if="data.allowFreeText"
          class="absolute flex items-center gap-1"
          :style="{ top: `${calculateHandleTop(1)}px`, right: '-8px' }"
          @mouseenter="hoveredOption = 'other'"
          @mouseleave="hoveredOption = null"
        >
          <Handle
            id="handle_other"
            type="source"
            :position="Position.Right"
            :class="[
              'w-3 h-3 !border-2 !border-white transition-all',
              hoveredOption === 'other' ? '!bg-orange-300 !w-4 !h-4 !shadow-lg !shadow-orange-400/50' : '!bg-orange-400'
            ]"
            v-tooltip.top="$t('flowEditor.nodes.botAsksNode.freeTextHandleTitle')"
          />
          <span
            class="text-[10px] bg-purple-900/90 px-1 rounded-sm whitespace-nowrap pointer-events-none transition-opacity"
            :class="hoveredOption === 'other' ? 'opacity-100' : 'opacity-70'"
          >
            {{ $t('flowEditor.nodes.botAsksNode.otherLabel') }}
          </span>
        </div>
      </template>
      <template v-else>
        <div
          v-for="(option, idx) in data.options"
          :key="option.id"
          class="absolute flex items-center gap-1"
          :style="{ top: `${calculateHandleTop(idx)}px`, right: '-8px' }"
          @mouseenter="hoveredOption = option.id"
          @mouseleave="hoveredOption = null"
        >
          <Handle
            :id="`handle_${option.id}`"
            type="source"
            :position="Position.Right"
            :class="[
              'w-3 h-3 !border-2 !border-white transition-all',
              hoveredOption === option.id ? '!bg-cyan-300 !w-4 !h-4 !shadow-lg !shadow-cyan-400/50' : '!bg-cyan-400'
            ]"
            v-tooltip.top="option.label"
          />
          <span
            class="text-[10px] bg-purple-900/90 px-1 rounded-sm whitespace-nowrap pointer-events-none transition-opacity"
            :class="hoveredOption === option.id ? 'opacity-100' : 'opacity-70'"
          >
            {{ useNumbering ? `${idx + 1}` : truncateLabel(option.label, 8) }}
          </span>
        </div>
        <div
          v-if="data.allowFreeText"
          class="absolute flex items-center gap-1"
          :style="{ top: `${calculateHandleTop(data.options.length)}px`, right: '-8px' }"
          @mouseenter="hoveredOption = 'other'"
          @mouseleave="hoveredOption = null"
        >
          <Handle
            id="handle_other"
            type="source"
            :position="Position.Right"
            :class="[
              'w-3 h-3 !border-2 !border-white transition-all',
              hoveredOption === 'other' ? '!bg-orange-300 !w-4 !h-4 !shadow-lg !shadow-orange-400/50' : '!bg-orange-400'
            ]"
            v-tooltip.top="$t('flowEditor.nodes.botAsksNode.freeTextHandleTitle')"
          />
          <span
            class="text-[10px] bg-purple-900/90 px-1 rounded-sm whitespace-nowrap pointer-events-none transition-opacity"
            :class="hoveredOption === 'other' ? 'opacity-100' : 'opacity-70'"
          >
            {{ $t('flowEditor.nodes.botAsksNode.otherLabel') }}
          </span>
        </div>
      </template>
    </template>
    <!-- Single source handle for text-only response -->
    <Handle
      v-else
      type="source"
      :position="Position.Right"
      class="w-3 h-3 !bg-cyan-400 !border-2 !border-white"
    />
    <!-- Add node button -->
    <AddNodeButton v-if="!hasOutgoingEdge" @add="emit('add-node', nodeId)" />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { Handle, Position } from '@vue-flow/core';
import { useI18n } from 'vue-i18n';
import AddNodeButton from './AddNodeButton.vue';
import ValidationMarker from './ValidationMarker.vue';

const { t } = useI18n();

const emit = defineEmits(['add-node']);

const props = defineProps({
  data: { type: Object, required: true },
  selected: { type: Boolean, default: false },
  nodeId: { type: String, required: true },
  hasOutgoingEdge: { type: Boolean, default: false },
});

const hoveredOption = ref(null);

const hasOptions = computed(() => {
  return props.data.responseType !== 'text' && 
         props.data.options && 
         props.data.options.length > 0;
});

const useNumbering = computed(() => {
  return props.data.options && props.data.options.length > 6;
});

const truncateLabel = (text, maxLength) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

const getResponseIcon = (type) => {
  const icons = {
    text: 'text_fields',
    buttons: 'smart_button',
    select: 'arrow_drop_down_circle',
    multiselect: 'checklist',
  };
  return icons[type] || 'help_outline';
};

const getResponseLabel = (type) => {
  const key = `flowEditor.nodes.botAsksNode.responseType.${type}`;
  return t(key, t('flowEditor.nodes.botAsksNode.responseType.notConfigured'));
};

const calculateHandleTop = (index) => {
  // Header: ~32px, body padding: ~12px, space between handles
  const headerHeight = 32;
  const bodyPadding = 12;
  const handleSpacing = 28; // Space between handles
  return headerHeight + bodyPadding + (index * handleSpacing);
};
</script>
